import { RenderTemplateCallback, SignatureProps, SingleHeadlineProps } from "../../src/types";
import { colors, TemplateKeys, backgrounds, watermarks, content } from "./templates/shared";

export const renderTemplate: RenderTemplateCallback = async (ctx, utils, template, data, signatures) => await renderTest(ctx, utils, template, data, signatures); 

const renderHeadline = (text: string, name: string, props: SingleHeadlineProps, text2?: string) => {
    const font = props.utils.fontScaler(props.template);
    const scale = props.utils.templateScaler(props.template);
    props.ctx.font = font(props.template.font[name].size, props.template.font[name].family);
    let shm = props.ctx.measureText(text);
    let headlineText = text;


    if (text2) {
        headlineText = `${text}\n${text2}`;
        const longestLine = headlineText.split('\n').reduce((longest, line) => {
            if (line.length > longest.length) {
                return line;
            }
            return longest;
        }, '');
        shm = props.ctx.measureText(longestLine);
    }

    const centerOffset = props.textLeftMargin - (props.contentCentered ? 0 : props.contentWidth / 2) - (shm.width / 2);
    let headMargin = 0;

    if (props.template.font[name].headMarginLeft) {
        headMargin = props.template.font[name].headMarginLeft;
    }

    if (props.template.font[name].headMarginRight) {
        headMargin = props.template.font[name].headMarginRight;
    }

    props.utils.renderText(props.ctx, {
        font: props.ctx.font,
        text: headlineText,
        fillStyle: props.template.font[name].color,
        x: centerOffset + headMargin,
        y: name === 'subHeadline' ? props.template.font.content.TopPadding + Math.max(scale(props.template.font.subHeadline.TopPadding), props.contentHeight + scale(props.template.font.subHeadline.yScale2)) : scale(props.template.font[name].TopPadding),
        lineHeight: scale(props.template.font[name].lineHeight) ? scale(props.template.font[name].lineHeight) : undefined,
        centered: props.contentCentered
    });
}


export const renderSignature = (signatures, props: SignatureProps) => {
    const font = props.utils.fontScaler(props.template);
    const scale = props.utils.templateScaler(props.template);
    let left = props.textLeftMargin - props.contentWidth / 2;
    let top = props.template.signature.topPadding + (props.template.signature.contentDep ? Math.max(scale(60), props.contentHeight + scale(60)) : 0);
    const singleSignature = props.template.signature.singleSignature;

    for (const signature of signatures) {
        props.ctx.save();
        const m = props.ctx.measureText(signature.signature);
        let willBreak = false;
        if (left + m.width + scale(60)  > props.contentWidth + props.textLeftMargin / 2) {
            willBreak = true;
        }
        props.ctx.restore();
        const measure = props.utils.renderSignature(props.ctx, {
            signature: {
                font: font(props.template.signature.fontSize, 'AlFresco'),
                text: signature.signature,
                fillStyle: props.template.signature.color
            },
            subtitle: {
                font: font(props.template.signature.fontSizeSub, 'Poppins-SemiBold'),
                text: signature.subtitle,
                fillStyle: props.template.signature.color,
                offset: scale(25)
            },
            centered: true,

            x: singleSignature ? props.textLeftMargin - m.width : willBreak ? props.textLeftMargin - (props.contentWidth / 2) : left,
            y: top + (willBreak ? scale(80) : 0)
        })
        left += measure.width + scale(20);
        if (left + scale(50) > props.textLeftMargin + props.contentWidth) {
            top += measure.height + scale(80);
            left = props.textLeftMargin - (props.contentWidth / 2) + (willBreak ? measure.width + scale(20) : 0);
        }
    }
}

export const renderTest: RenderTemplateCallback = async (ctx, utils, template, data, signatures) => {

    const templateKey = template.key as TemplateKeys;
    const backgroundImg = await utils.getImage(backgrounds[templateKey]);
    const watermarkImg = await utils.getImage(watermarks[templateKey]);

    const scale = utils.templateScaler(template);
    const font = utils.fontScaler(template);
    const image = utils.imageScaler(template);

    const width = scale(1, '%w');
    const height = scale(1, '%h');

    ctx.clearRect(0, 0, width, height);

    ctx.fillStyle = colors[templateKey].background;
    ctx.fillRect(0, 0, width, height);

    image(ctx, {
        image: backgroundImg,
        left: 0,
        top: 0,
        scale: 0.5
    });

    image(ctx, {
        image: watermarkImg,
        left: 0,
        top: 0,
        scale: 0.5
    });



    let contentTopPadding = 0;
    const contentCentered = content[templateKey].centered;
    const contentWidth = scale(content[templateKey].width);
    const textLeftMargin = scale(content[templateKey].left);

    ctx.save();

    contentTopPadding = template.font.content && template.font.content.TopPadding;
    const { height: contentHeight } = utils.renderText(ctx, {
        font: font(template.font.content.size, template.font.content.family),
        text: utils.getText(data.content).trim(),
        fillStyle: template.font.content.color,
        x: textLeftMargin - (contentCentered ? contentWidth / 2 : 0),
        y: contentTopPadding,
        lineHeight: scale(template.font.content.lineHeight),
        maxWidth: contentWidth,
        centered: contentCentered
    });


    

    const headlineTop = template.font.headlineTop && utils.getText(data.headlineTop).trim();
    const headlineBottom = template.font.headlineBottom && utils.getText(data.headlineBottom).trim();
    const subHeadline = template.font.subHeadline && utils.getText(data.subHeadline).trim();
    const subHeadline1 = template.font.subHeadline1 && utils.getText(data.subHeadline1).trim();
    const subHeadline2 = template.font.subHeadline2 && utils.getText(data.subHeadline2).trim();
    const subHeadline3 = template.font.subHeadline3 && utils.getText(data.subHeadline3).trim();
    const subHeadline4 = template.font.subHeadline4 && utils.getText(data.subHeadline4).trim();

    const props: SingleHeadlineProps = {
        template,
        ctx,
        utils,
        textLeftMargin,
        contentCentered,
        contentWidth,
        templateKey,
        contentHeight
    }

    headlineTop && headlineBottom && template.font.doubleHeadline ? renderHeadline(headlineTop, 'headlineTop', props, headlineBottom) : undefined;
    headlineTop && !template.font.doubleHeadline ? renderHeadline(headlineTop, 'headlineTop', props) : undefined;
    headlineBottom && !template.font.doubleHeadline ? renderHeadline(headlineBottom, 'headlineBottom', props) : undefined;
    subHeadline && renderHeadline(subHeadline, 'subHeadline', props);
    subHeadline1 && renderHeadline(subHeadline1, 'subHeadline1', props);
    subHeadline2 && renderHeadline(subHeadline2, 'subHeadline2', props);
    subHeadline3 && !template.font.doubleSubHeadline ? renderHeadline(subHeadline3, 'subHeadline3', props) : undefined;
    subHeadline4 && !template.font.doubleSubHeadline ? renderHeadline(subHeadline4, 'subHeadline4', props) : undefined;
    subHeadline3 && subHeadline4 && template.font.doubleSubHeadline ? renderHeadline(subHeadline3, 'subHeadline3', props, subHeadline4) : undefined;

    ctx.restore();

    renderSignature(signatures, props);
}
